import { computed, type ComputedRef } from 'vue'
import { useFeaturesStore } from '@/stores/features'
import { useBillingStore } from '@/stores/billing'

export interface CreditGateBlockedOptions {
    /** Action key — dialog routing + analytics elsewhere. Not part of the
     * blocking decision itself. */
    action: string
    /** Skip the check entirely (e.g. IA has already consumed AI quota
     * upstream) — always wins, regardless of plan or balance. */
    bypass?: boolean
    /** Unit count for per-unit actions (e.g. changed clauses for
     * `gap_analysis_per_clause`, regulations for `regulation_watch_per_norm`).
     * With an estimate key and a count, the gate blocks below
     * `estimate × count` — the deliberately low estimate; without, at 0. */
    count?: number
}

/**
 * The `$$$` gate's single blocked rule (front CLAUDE.md), in ONE place.
 *
 * Policy (Aymeric, 2026-09-11, replacing the flat threshold of 2026-08-17): on
 * a credit plan the gate blocks below `max(1, estimate × count)` — 0 credits
 * blocks every paid action; an action whose cost follows a known count blocks
 * below its deliberately LOW estimate. Non-credit plans keep the quota rule
 * (`!featuresStore.canStartAssessment`). `bypass` always wins → never
 * blocked.
 *
 * Pass `opts` as the live reactive source (e.g. a component's own `props`
 * object from `defineProps`) when `bypass` can change at runtime — a fresh
 * object literal captures its values once at call time and won't track
 * later prop changes. A static literal (constant `action`, no `bypass`) is
 * fine when there is nothing to react to, as in a persistent-input's
 * `send()` guard.
 */
export function useCreditGateBlocked(opts: CreditGateBlockedOptions): ComputedRef<boolean> {
    const featuresStore = useFeaturesStore()
    const billingStore = useBillingStore()

    return computed(() => {
        if (opts.bypass) return false
        if (featuresStore.isCreditPlan) {
            // "Not yet loaded" must never read as blocked (see the billing
            // store's own null-guard).
            if (!billingStore.creditBalance) return false
            // Aymeric, 2026-09-11 — the credit-gate rule (front CLAUDE.md, `$$$`):
            // every paid action blocks at 0 credits; an action whose cost follows
            // a known count blocks below its deliberately LOW estimate
            // (`estimates[action] × count`) — not to guarantee the credits
            // suffice (a small negative balance is fine) but to refuse a run
            // that is SURE not to finish. The estimate is never shown.
            const unit = billingStore.creditEstimates?.[opts.action]
            const count = opts.count
            const lowEstimate = typeof unit === 'number' && typeof count === 'number' && count > 0
                ? unit * count
                : 0
            return billingStore.totalCredits < Math.max(1, lowEstimate)
        }
        return !featuresStore.canStartAssessment
    })
}
